<template>
  <div class="complaint-submit">
    <el-card>
      <template #header>
        <div class="card-header">
          <h2>投诉提交</h2>
          <p>请如实填写投诉信息，我们会尽快处理</p>
        </div>
      </template>

      <el-form ref="formRef" :model="form" :rules="rules" label-width="120px" size="large">
        <el-form-item label="投诉对象" prop="targetType">
          <el-radio-group v-model="form.targetType">
            <el-radio :label="1">个人会员</el-radio>
            <el-radio :label="2">机构会员</el-radio>
            <el-radio :label="3">其他</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item v-if="form.targetType === 1 || form.targetType === 2" label="选择对象" prop="targetId">
          <el-select v-model="form.targetId" placeholder="请选择投诉对象" filterable remote :remote-method="searchMembers"
            :loading="searchLoading" style="width: 100%">
            <el-option v-for="member in memberOptions" :key="member.id" :label="member.name" :value="member.id">
              <span>{{ member.name }}</span>
              <span style="float: right; color: #8492a6; font-size: 13px">
                {{ member.phone }}
              </span>
            </el-option>
          </el-select>
        </el-form-item>

        <el-form-item v-if="form.targetType === 3" label="对象名称" prop="targetName">
          <el-input v-model="form.targetName" placeholder="请输入投诉对象名称" />
        </el-form-item>

        <el-form-item label="投诉类型" prop="complaintType">
          <el-select v-model="form.complaintType" placeholder="请选择投诉类型" style="width: 100%">
            <el-option label="服务质量问题" value="服务质量问题" />
            <el-option label="职业道德问题" value="职业道德问题" />
            <el-option label="收费问题" value="收费问题" />
            <el-option label="虚假宣传" value="虚假宣传" />
            <el-option label="其他" value="其他" />
          </el-select>
        </el-form-item>

        <el-form-item label="投诉标题" prop="title">
          <el-input v-model="form.title" placeholder="请输入投诉标题" maxlength="100" show-word-limit />
        </el-form-item>

        <el-form-item label="投诉内容" prop="content">
          <el-input v-model="form.content" type="textarea" :rows="6" placeholder="请详细描述投诉内容" maxlength="1000"
            show-word-limit />
        </el-form-item>

        <el-form-item label="联系方式" prop="contact">
          <el-input v-model="form.contact" placeholder="请输入您的联系方式（手机号或邮箱）" />
        </el-form-item>

        <el-form-item label="附件上传">
          <el-upload :action="uploadUrl" :headers="uploadHeaders" :limit="5" list-type="picture-card"
            :on-success="handleUploadSuccess" :on-error="handleUploadError" :on-remove="handleRemove"
            :file-list="fileList">
            <el-icon>
              <Plus />
            </el-icon>
          </el-upload>
          <div class="upload-tip">
            最多上传5个文件，支持图片、音频、视频格式
          </div>
        </el-form-item>

        <el-form-item>
          <el-checkbox v-model="form.agreed">
            我已阅读并同意
            <el-link type="primary" @click="showAgreement">《投诉处理规则》</el-link>
          </el-checkbox>
        </el-form-item>

        <el-form-item>
          <el-button type="primary" :loading="submitting" @click="handleSubmit">
            {{ submitting ? '提交中...' : '提交投诉' }}
          </el-button>
          <el-button @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 投诉规则弹窗 -->
    <el-dialog v-model="agreementVisible" title="投诉处理规则" width="600px">
      <div class="agreement-content">
        <p>1. 投诉人应如实提供投诉信息，不得捏造事实。</p>
        <p>2. 协会将在收到投诉后5个工作日内进行调查核实。</p>
        <p>3. 投诉处理结果将通过您提供的联系方式告知。</p>
        <p>4. 对于恶意投诉，协会保留追究法律责任的权利。</p>
      </div>
      <template #footer>
        <el-button type="primary" @click="agreementVisible = false">
          我已阅读
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { Plus } from '@element-plus/icons-vue'
import complaintApi from '@/api/complaint'
import attachment from '@/api/public'
import { mapGetters } from 'vuex'

export default {
  name: 'ComplaintSubmit',

  components: {
    Plus
  },

  computed: {
    ...mapGetters('auth', ['token']),

    uploadHeaders() {
      return {
        Authorization: this.token || 'no-token'
      }
    }
  },

  data() {
    // 联系方式验证
    const validateContact = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请输入联系方式'))
      } else {
        // 验证是否为手机号或邮箱
        const mobilePattern = /^1[3-9]\d{9}$/
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

        if (mobilePattern.test(value) || emailPattern.test(value)) {
          callback()
        } else {
          callback(new Error('请输入正确的手机号或邮箱'))
        }
      }
    }

    return {
      uploadUrl: '',
      form: {
        targetType: 1,
        targetId: null,
        targetName: '',
        complaintType: '',
        title: '',
        content: '',
        contact: '',
        attachments: [],
        agreed: false
      },
      rules: {
        targetType: [
          { required: true, message: '请选择投诉对象类型', trigger: 'change' }
        ],
        targetId: [
          { required: true, message: '请选择投诉对象', trigger: 'change' }
        ],
        targetName: [
          { required: true, message: '请输入对象名称', trigger: 'blur' }
        ],
        complaintType: [
          { required: true, message: '请选择投诉类型', trigger: 'change' }
        ],
        title: [
          { required: true, message: '请输入投诉标题', trigger: 'blur' }
        ],
        content: [
          { required: true, message: '请输入投诉内容', trigger: 'blur' },
          { min: 20, message: '投诉内容至少20个字', trigger: 'blur' }
        ],
        contact: [
          { required: true, validator: validateContact, trigger: 'blur' }
        ]
      },
      memberOptions: [],
      searchLoading: false,
      submitting: false,
      agreementVisible: false,
      fileList: [] // 文件列表
    }
  },

  created() {
    attachment.uploadFileUrl().then(res => {
      this.uploadUrl = res
    })
  },

  methods: {
    handleUploadSuccess(response, file) {
      if (response && response.code === 2000 && response.data) {
        this.form.attachments.push(response.data.url)
        this.$message.success('文件上传成功')
      } else {
        this.$message.error('文件上传失败')
        // 移除上传失败的文件
        const index = this.fileList.findIndex(f => f.uid === file.uid)
        if (index > -1) {
          this.fileList.splice(index, 1)
        }
      }
    },

    handleUploadError() {
      this.$message.error('文件上传失败，请重试')
    },

    handleRemove(file) {
      // 从附件列表中移除对应的URL
      const index = this.fileList.findIndex(f => f.uid === file.uid)
      if (index > -1) {
        this.form.attachments.splice(index, 1)
      }
    },
    /**
     * 搜索会员
     */
    async searchMembers(query) {
      if (!query) {
        this.memberOptions = []
        return
      }

      this.searchLoading = true

      try {
        const res = await complaintApi.getComplainableMembers({
          keyword: query,
          member_type: this.form.targetType
        })

        if (res && res.code === 2000) {
          this.memberOptions = res.data || []
        }
      } catch (error) {
        console.error('搜索会员失败:', error)
      } finally {
        this.searchLoading = false
      }
    },

    /**
     * 提交投诉
     */
    async handleSubmit() {
      // 验证表单
      const valid = await this.$refs.formRef.validate().catch(() => false)
      if (!valid) return

      // 检查是否同意规则
      if (!this.form.agreed) {
        this.$message.warning('请先阅读并同意投诉处理规则')
        return
      }

      this.submitting = true

      try {
        // 准备提交数据
        const submitData = {
          target_type: this.form.targetType,
          complained_id: this.form.targetId, // 投诉对象ID
          complaint_type: this.form.complaintType,
          title: this.form.title,
          content: this.form.content,
          contact: this.form.contact,
          attachments: this.form.attachments.join(',') // 多个附件用逗号分割
        }

        // 其他类型时才传 target_name
        if (this.form.targetName) {
          submitData.target_name = this.form.targetName
        }

        const res = await complaintApi.submitComplaint(submitData)

        if (res && res.code === 2000) {
          this.$message.success('投诉提交成功，我们会尽快处理')

          // 重置表单
          this.handleReset()

          // 跳转到首页或投诉列表
          setTimeout(() => {
            this.$router.push('/user/home')
          }, 1500)
        }
      } catch (error) {
        console.error('提交投诉失败:', error)
      } finally {
        this.submitting = false
      }
    },

    /**
     * 重置表单
     */
    handleReset() {
      this.$refs.formRef.resetFields()
      this.form.attachments = []
      this.fileList = []
      this.memberOptions = []
    },

    /**
     * 显示投诉规则
     */
    showAgreement() {
      this.agreementVisible = true
    }
  }
}
</script>

<style lang="scss" scoped>
.complaint-submit {
  min-height: 100vh;
  padding: 20px;
  background-color: #f5f7fa;

  .el-card {
    max-width: 900px;
    margin: 0 auto;

    .card-header {
      text-align: center;

      h2 {
        margin: 0 0 10px 0;
        font-size: 24px;
        color: #303133;
      }

      p {
        margin: 0;
        color: #909399;
        font-size: 14px;
      }
    }
  }

  .upload-tabs {
    width: 100%;
  }

  .upload-tip {
    margin-top: 8px;
    font-size: 12px;
    color: #909399;
  }

  .agreement-content {
    line-height: 2;
    color: #606266;

    p {
      margin: 10px 0;
    }
  }
}

@media (max-width: 768px) {
  .complaint-submit {
    padding: 10px;

    :deep(.el-form-item__label) {
      width: 100px !important;
    }
  }
}
</style>
